/**
 * Vanity-alias shape policy for the SHARED root namespace.
 *
 * Orion Meet vanity URLs live on the SPA root (`/:alias`), the SAME
 * react-router pattern as a chat (`/:chatId`). One component owns that segment
 * and decides chat-vs-vanity purely by SHAPE — no network round-trip:
 *
 *   - chatId = uuidv7: always 36 chars, `8-4-4-4-12` hex with dashes.
 *   - alias  = `^[a-z0-9][a-z0-9-]{2,31}$`: 3–32 chars, lowercased.
 *
 * The alias length is capped at 32 (< 36) PRECISELY so an alias can never have
 * a uuid's length — a real chatId therefore can never be mis-routed to the
 * vanity join. `isVanityAliasSegment` still excludes uuids explicitly as
 * belt-and-suspenders and to keep the resolution order (uuid → chat first)
 * legible.
 *
 * Mirrors the backend `aliasPolicy.ts` charset (ow2-chat-be). The BACKEND is
 * the authority for what is claimable/reserved; this regex is only for instant
 * client-side routing + form feedback.
 */

/** 36-char canonical UUID (any version — we never mis-route a chatId). */
export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Vanity alias charset: 3–32 chars, `[a-z0-9]` first, then `[a-z0-9-]`. */
export const ALIAS_REGEX = /^[a-z0-9][a-z0-9-]{2,31}$/;

/** Canonical (lowercase) form used for minting/resolving — BE stores lowercase. */
export function canonicalizeAlias(segment: string): string {
  return segment.toLowerCase();
}

/** True when `value` (lowercased) is a syntactically valid vanity alias. */
export function isAliasShape(value: string): boolean {
  return ALIAS_REGEX.test(value.toLowerCase());
}

/** True when `value` is a canonical UUID (a chatId), case-insensitively. */
export function isUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

/**
 * The load-bearing routing predicate: does this root segment resolve to the
 * vanity meet join rather than a chat? A uuid (chatId) is ALWAYS chat; anything
 * matching the alias charset (and not a uuid) is a vanity alias.
 */
export function isVanityAliasSegment(segment: string): boolean {
  return !isUuid(segment) && isAliasShape(segment);
}
